import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from "@mui/material";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useNavigate } from "Navigation/useNavigate";
import type { RootStateType } from "State/Store";
import { deactivateUserAsync } from "State/Users/Detail/DeactivateUserState";
import { useDispatch, useSelector } from "react-redux";
import { Resources, useResource } from "Translations/Resources";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { ROLES_ALLOWED_TO_DEACTIVATE_USER } from "Components/UserAccount/constants";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const RESOURCES = Resources.Components.Account.DeactivateUser;

export const DeactivateAccountButton = () => {
  const { handleResult, isOpen, open } = useConfirmDialog();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useResource();
  const userRole = useCurrentUserRole();
  const isDeactivatingUser = useSelector(
    ({ user }: RootStateType) => user.isDeactivatingUser,
  );

  const canDeactivateAccount = userRole
    ? ROLES_ALLOWED_TO_DEACTIVATE_USER.includes(userRole)
    : false;

  if (!canDeactivateAccount) {
    return <ErrorText>{t(Resources.Error.NoAccess)}</ErrorText>;
  }

  const deactivate = () => {
    dispatch(deactivateUserAsync.request({ navigate }));
  };

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <PaperTitle>{t(RESOURCES.Title)}</PaperTitle>
        <Typography>{t(RESOURCES.Description)}</Typography>
        <Accordion
          disableGutters
          sx={{
            bgcolor: "transparent",
            boxShadow: "none",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography fontWeight={500}>
              {t(RESOURCES.InfoSection.Title)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack gap={2}>
              <Typography>{t(RESOURCES.InfoSection.Paragraph1)}</Typography>
              <Typography>{t(RESOURCES.InfoSection.Paragraph2)}</Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <PrimaryButton
        fullWidth
        color="error"
        onClick={open}
        variant="contained"
        disabled={isDeactivatingUser}
      >
        {t(RESOURCES.DeactivateButton)}
      </PrimaryButton>
      <ConfirmDialog
        isDestructive
        isOpen={isOpen}
        modalTitle={t(RESOURCES.DeactivateButton)}
        modalContent={<Typography>{t(RESOURCES.Confirmation)}</Typography>}
        handleResult={handleResult(deactivate)}
      />
    </Stack>
  );
};
